// Dice COMPOSITION ROOT (boot). The only thing this frontend module adds on top of the vendored CoinPoker game
// (public/vendor/coinpoker-dice/, window.CPDice, booted by its own js/the-handles.js) is: a fail-closed contract
// self-check, the SDK boot (through the trust-layer bootstrap), and the round driver + provably-fair verifier,
// installed into the game from OUTSIDE via window.CPDice — the same seam the product's games/Dice/operator.js uses for the
// original CoinPoker bridge. The vendor is byte-identical; the testable operator logic lives in ./operator.
//
// Two entries boot through here (SC-54713): main.ts (Dice's own page, where the vendored game's classic scripts ran
// before this module) and shell-entry.ts (rgs-core-api-gw's /play/dice shell, which takes the launch FIRST, then mounts
// the page and loads the vendored game before it calls bootDice).
//
// Re-homed onto the frontend-split architecture (Goal A): it talks ONLY to the opaque GameSdk
// (play/on/reportResultsShown) and re-derives the roll from the settled round's provably-fair reveal via the
// CONTRACT (@blocklabsdev/rgs-contracts diceResolve). It owns ZERO outcome/money math.
//
// ---- MONEY-PATH RULING (read before touching the balance wiring below) --------------------------
// The vendor's own doRoll/runRoll/settle (the-handles.js) ALREADY debit the stake and credit the payout locally. Once a
// driver is installed, the-handles.js's settle() credits the DRIVER's `payoutMinor` — the RGS engine's OWN floor-toward-
// house payout (RULING R3) — NOT its local round-half-up payoutCents, so the vendor's running balance and the RGS
// agree BY CONSTRUCTION for a ROUND. That arithmetic is the round's animation, and it is blind to everything else (a
// deposit, a bet in another tab). So (SC-62290, balance-sync.ts): the session balance is pushed into the game at boot
// (session.ready / session.restored), and from then on, WHENEVER NO ROUND IS ON SCREEN, the board shows the balance
// the SDK holds — the server's. Never while a roll is on screen: the SDK emits a round's balance INSIDE play() (before
// the driver's promise resolves), and writing it onto S.balance then would land ON TOP OF the vendor's own debit and
// credit — a silent double count. The money is authoritative server-side either way.

import { checkPfBinding, type GameSdk, type PfBindingResult, type PfRoundEvidence, type SessionReadyPayload } from "@blocklabsdev/rgs-game-manager-frontend-api";
import { takeDiceLaunch, createDiceSDK, type DiceLaunch } from "./bootstrap";
import { makeRollDriver, makeVerifier, type RoundRecord } from "./operator";
import { renderFairPanel } from "./pf/panel";
import { verifyTicketRound } from "./pf/ticket-verify";
import { trackPfTickets } from "./pf/tickets";
import { makeMaxStake } from "./max-stake";
import { settlementShown, type SettlementShown } from "./pf/settlement";
import { DICE_PREDECESSOR_HASHES, DICE_V2_MATH_IDS, PINNED_DICE_CONTRACT_HASH, selfCheckContract, type SelfCheckResult } from "./contract";
import { blockKeysWhileCovered, boardCovered, revealBoot } from "./loader";
import { currencyNotice, fatalNotice } from "./fatal-notice";
import { displayableCode, sessionMoney, type SessionMoney } from "./currency";
import { holdOnOperatorSignals } from "./operator-signals";
import { installOverlayStyle } from "./overlay-style";
import { installBoardStyle } from "./board-style";
import { followStakeRail, refusalToast } from "./refusal-toast";
import { apiBase, apiOrigin } from "./api-origin";
import { keepFocusInSheet } from "./sheet-focus";
import { reflowOnResize } from "./board-reflow";
import { syncBalance } from "./balance-sync";
import { buildLabel } from "./telemetry";

declare global {
  interface Window {
    CPDice?: any;
  }
}

// Fail-closed block (SC-57555). Replaces the whole app subtree so the undriven vendor cannot be played with a
// local engine the server did not run: without a driver, the-handles.js would fall back to its OWN local model (half-up
// payout + local PF) and present a fairness "proof" we cannot honour. A wrong proof is worse than none.
// The panel sits ABOVE the SDK boot loader (its root is z-index 2147483000): on a failed REAL launch the loader shows
// a generic error + Retry, and only this panel says what actually works (relaunch from the casino).
const BLOCK_PANEL_Z = "2147483647";

// `reload` adds a Reload button: only for a failure a reload can recover (fatal-notice.ts decides when).
// `keepBoard` (SC-62084): a fatal that arrives once the game is DRIVEN (the platform ended the session mid-play) keeps the
// board mounted, frozen and unreachable under the panel: the round the SDK just refused is still settling into it (the
// vendor's rejection branch repaints the CTA and writes its message), so tearing it down would leave that code writing
// into removed nodes. Every boot path tears the UN-driven board down instead: its local model must never be playable.
function blockApp(title: string, lines: readonly string[], code?: string, reload?: () => void, keepBoard = false): void {
  revealBoot(document);
  // ONE notice at a time: a later one supersedes the one on screen (a session that ends after the game stopped for an
  // unconfirmed round: the relaunch notice replaces the reload one — a reload cannot revive that session). Stacked
  // panels would leave two alerts in the page and the wrong one possibly on top.
  for (const shown of Array.from(document.querySelectorAll(".contract-block"))) shown.remove();
  if (keepBoard) {
    // No focus, no pointer, out of the accessibility tree: #app AND its siblings — the vendor's sheet (Settings, Autobet,
    // Provably fair) is a non-modal <dialog> outside #app, and the SDK's own overlays hang off <body> too.
    for (const el of Array.from(document.body.children)) el.setAttribute("inert", "");
  } else {
    document.getElementById("app")?.replaceChildren(); // tear the un-driven vendor UI down
  }
  const panel = document.createElement("div");
  panel.className = "contract-block";
  panel.setAttribute("role", "alert");
  // The notice is centred when it fits and SCROLLS when it does not (a short landscape phone, SC-61819): the content
  // sits in a box with auto margins inside a scrolling flex panel — centring with justify-content would cut its top and
  // its bottom off, out of reach, Reload included.
  panel.style.cssText =
    "position:fixed;inset:0;display:flex;overflow:auto;padding:24px 32px;box-sizing:border-box;text-align:center;" +
    "background:#010D06;color:#e8eef7;font:16px/1.5 var(--ui, system-ui, sans-serif);text-wrap:balance";
  panel.style.zIndex = BLOCK_PANEL_Z;
  const content = document.createElement("div");
  content.style.cssText = "margin:auto;display:flex;flex-direction:column;gap:12px;align-items:center";
  panel.appendChild(content);
  const h = document.createElement("h1");
  h.textContent = title;
  h.style.cssText = "font-size:22px;margin:0";
  content.appendChild(h);
  for (const line of lines) {
    const p = document.createElement("p");
    p.textContent = line;
    p.style.cssText = "max-width:44ch;margin:0;color:#aab6c8";
    content.appendChild(p);
  }
  let button: HTMLButtonElement | undefined;
  if (reload) {
    button = document.createElement("button");
    button.type = "button";
    button.textContent = "Reload";
    button.style.cssText =
      "margin-top:4px;padding:10px 28px;border:0;border-radius:8px;background:#C0180C;color:#fff;cursor:pointer;" +
      "font:600 15px/1 var(--ui, system-ui, sans-serif)";
    button.addEventListener("click", reload);
    content.appendChild(button);
  }
  if (code) {
    const c = document.createElement("code");
    c.textContent = code;
    c.style.cssText = "font-size:12px;color:#7f8da0;opacity:.8";
    content.appendChild(c);
  }
  document.body.appendChild(panel);
  // Focus follows the notice (its Reload when there is one), so the keyboard never stays on a control behind it.
  panel.tabIndex = -1;
  (button ?? panel).focus();
}

/** The launch, taken before anything else happens in the document (takeLaunchEarly). */
export interface EarlyLaunch {
  readonly launch: DiceLaunch;
  /** True once a launch change or a back/forward restore asked for a reload: the outgoing document starts no round. */
  readonly rebooting: () => boolean;
  /**
   * Hands over the SDK once it exists (SC-61840): a reload asked for from then on stops it first (`sdk.freeze()`), and
   * one that was already asked for stops it at once.
   */
  readonly stops: (sdk: Pick<GameSdk, "freeze">) => void;
}

// Decide the money mode from the LAUNCH and take the single-use ticket SYNCHRONOUSLY, before the first await and before
// any network request (SC-61814, SC-54713): bootstrap takes it out of the URL at once, so it never waits there for the
// contract check, the shell's asset loads or the SDK boot. A launch change or a back/forward restore reloads the page
// (bootstrap); until the new document commits this one keeps running, so it is frozen first: the vendor pauses (once it
// exists — in the shell it may still be loading), the driver refuses every round (autobet included), and the SDK is
// stopped (SC-61840): a round the driver had already let through may still be loading its key or signing its request
// inside sdk.play() — frozen, the SDK does not dispatch it, and aborts one already sent (its outcome is then unknown;
// the new document re-reads the balance). Without that, such a wager left a few milliseconds after the switch.
export function takeLaunchEarly(): EarlyLaunch {
  // The build says what it is, once, on every load (SC-62248): the release (or 0.0.0-dev+<commit>) and the
  // environment it was built for — what a bug report or a QA note needs to name.
  console.info(`[dice] build ${buildLabel()}`);
  let rebooting = false;
  let sdk: Pick<GameSdk, "freeze"> | null = null;
  const launch = takeDiceLaunch(() => {
    rebooting = true;
    sdk?.freeze(); // first: nothing more leaves the page on this session's money path
    pauseVendor();
  });
  return {
    launch,
    rebooting: () => rebooting,
    stops: (created) => {
      sdk = created;
      if (rebooting) created.freeze(); // the reload was asked for while the SDK was still booting
    },
  };
}

/**
 * Pauses the vendored game for a page that is leaving. Through the game's own setPaused while its board stands (the
 * CTA repaints to "Paused"); after a block path tore the board down (blockApp empties #app) there is nothing left to
 * repaint — setPaused would throw into the removed nodes — and the flag alone is what keeps the un-driven vendor frozen.
 */
function pauseVendor(): void {
  const G = window.CPDice;
  if (!G) return; // in the shell the vendored game may still be loading: it boots frozen
  if (document.getElementById("app")?.firstElementChild) G.setPaused?.(true);
  else G.S.paused = true;
}

/** The one notice for a boot that could not complete (a load or an unexpected error): never the error's own text. */
export function showBootFailure(early: EarlyLaunch): void {
  const n = fatalNotice({ realLaunch: early.launch.realLaunch, resumable: early.launch.kind === "real-resume" }, "launch_failed");
  blockApp(n.title, n.lines, "launch_failed");
}

/** A load that is not a launch (a bare URL on a launch-required build or in the gw shell, or the reload of a fresh REAL
 *  launch that never got its session, #mode=pending) runs NEITHER mode: no contract check, no SDK, no driver — only the
 *  casino notice. True when it was shown. */
export function showLaunchRequired(early: EarlyLaunch): boolean {
  if (early.launch.kind !== "launch-required") return false;
  console.warn("[dice] launch_required: the URL carries no launch mode");
  const n = fatalNotice({ realLaunch: false, resumable: false }, "launch_required");
  blockApp(n.title, n.lines, "launch_required");
  return true;
}

export async function bootDice(early: EarlyLaunch): Promise<void> {
  const { launch } = early;
  const G = window.CPDice;
  // FREEZE the vendor SYNCHRONOUSLY, first — before any block path tears the board down and before the first await.
  // the-handles.js is a classic script: it wired its own hotkeys (Space → doRoll) + click handlers at parse time (the
  // seam boots it with S.paused=true already; this keeps that true whatever ran in between). During the self-check fetch
  // + SDK boot below (both async), an un-driven Space/click would otherwise fall into the-handles.js's LOCAL fairness
  // model (local half-up payout + a locally-computed PF "proof") — exactly what the fail-closed block forbids. doRoll
  // bails while S.paused; it is cleared ONLY after the driver is installed, and never on any block path. Set directly, NOT
  // through setPaused: that repaints the board, and a block path removes it (a repaint into removed nodes throws).
  if (G) G.S.paused = true;
  if (showLaunchRequired(early)) return;
  if (!G) throw new Error("CPDice not booted (the vendored the-handles.js must run before bootDice)");
  installOverlayStyle(document); // the SDK's own system overlay (ui.blocked) must be visible above the board (SC-61815)
  installBoardStyle(document); // the refusal toast on short screens (SC-61819), the volume slider's target (SC-60141)

  // FUN BOOT SELF-CHECK (SC-57555) — BEFORE any RGS play is wired. Assert the same-origin contract manifest
  // (what the host/server declares it runs) equals the contract THIS bundle was built against
  // (PINNED_DICE_CONTRACT_HASH). FAIL CLOSED: on mismatch/missing/fetch-error, block and do NOT install a driver.
  const check: SelfCheckResult = await selfCheckContract(
    PINNED_DICE_CONTRACT_HASH,
    // SC-67335: the gateway's manifest — cross-origin from the game page on Dice's own site, never the site's own copy
    () => fetch(`${apiBase(apiOrigin())}/contract-manifest.json`, { cache: "no-store" }).then((r) => r.json()),
    "dice",
    DICE_PREDECESSOR_HASHES, // the expand/contract transition of the v1 identity (pinned-hash.json "transition")
  );
  if (!check.ok) {
    console.error("[dice] contract self-check FAILED — refusing to run:", check.reason, check);
    blockApp(
      "Dice unavailable",
      [
        "This build's provably-fair contract does not match the server. To protect fairness, the game will not run.",
        "Please relaunch — if this persists, the deployment is out of sync.",
      ],
      `pinned ${check.pinnedHash.slice(0, 10)} · served ${check.servedHash ? check.servedHash.slice(0, 10) : "none"}`,
    );
    return;
  }

  // The launch changed while the contract check was in flight: the page is reloading. The launch that was left opens
  // no session — a fresh REAL launch would exchange its ticket for one nobody will use.
  if (early.rebooting()) return;

  // A failed boot must SURFACE, never hang — and never show the error's own text to the player (it stays in the console).
  const resumable = launch.kind === "real-resume"; // a reload resumes this launch only when the URL says #mode=real
  let sdk: GameSdk;
  try {
    sdk = await createDiceSDK(launch);
  } catch (e) {
    console.error("[dice] SDK boot failed:", e);
    const n = fatalNotice({ realLaunch: launch.realLaunch, resumable }, "launch_failed");
    blockApp(n.title, n.lines, "launch_failed");
    return;
  }
  early.stops(sdk); // SC-61840: from here a launch change stops the SDK before the page reloads
  // SC-65843: the pf ticket state and proofs, heard from right after the factory (pf.ticket is replayed to a late
  // subscriber; a pf.proof that came during the boot waits for this first listener).
  const tickets = trackPfTickets(sdk);
  // SC-65243: the rounding fine print states how this session's rounds are paid — the fairness v1 settlement once the held
  // ticket's accepted terms say "stochastic", floor-toward-house before (pf/settlement.ts). Read when the help sheet opens.
  let settlement: SettlementShown = "floor";
  const showSettlement = (): void => {
    settlement = settlementShown(tickets.state(), settlement);
    G.setSettlement?.(settlement);
  };
  tickets.onChange(showSettlement);
  showSettlement();
  // SC-64981 (criterion 6): the stake is clamped to the largest the RGS admits for the selection — its winning roll within
  // the ceiling of the ticket held for the next roll (max-stake.ts). The game's own ceiling still applies, and with no
  // ticket it alone does (the RGS's refusal is then the only bound). Repainted when the ticket changes, so a stake above
  // a new ceiling is lowered before the next roll.
  const maxStake = makeMaxStake(() => tickets.state(), (listener) => void tickets.onChange(listener), () => sdk.pf?.ensure() ?? Promise.resolve({ mode: "legacy" }), undefined, DICE_V2_MATH_IDS);
  // The session's money (currency.ts), set from the session snapshot below; declared here, before the first reader (an
  // SDK event replayed on subscribe may read it at once).
  let money: SessionMoney | null = null;
  // The session's money as it stands (a later snapshot may renew it), for the round driver and the door. The boot stops
  // before the driver without one, so they never miss it.
  const sessionsMoney = (): SessionMoney => {
    if (!money) throw new Error("BOARD_NOT_READY");
    return money;
  };
  // The ceiling is the RGS's, in the wire's minor units; the game compares it with its stake, in hundredths (SC-68493). No
  // session money yet: no ceiling the board can use.
  G.setMaxStake?.((cU: number) => {
    const max = maxStake.rule(cU);
    return max === null || money === null ? null : money.toGame(max);
  });
  tickets.onChange(() => G.paint?.());
  // The launch changed while the SDK was booting: it is stopped (just above), the page is reloading. The boot goes no
  // further — no driver, no un-pause, the loader stays: never a live "Roll" on a document that is being replaced.
  if (early.rebooting()) return;
  // SDK boot loader (SC-61728): the SDK injected its loader inside createGameSDK — mark the real milestone, and
  // lift the static boot cover (index.html): from here the SDK loader covers the board (or, with ?loader=none,
  // nothing does and the board simply appears).
  sdk.loader.progress(0.6);
  revealBoot(document);

  // The verified rounds of this session, by ref (the provably-fair panel and CPDice.verify read them; SC-61813).
  const records = new Map<number, RoundRecord>();

  let fatal = false;
  let driven = false;
  let halted = false; // a settled round failed verification (SC-61813): nothing more is played until a reload

  // The session's money (SC-61816, currency.ts). The currency is the SESSION's — never a build constant, never a
  // default: null until the session snapshot arrives, and without one the boot stops (below). A currency the game's
  // two-decimal money model cannot represent must not be played: the game does not run (no driver; or, if the snapshot
  // ever came once driven, frozen under the notice). `money` itself is declared above, before the ceiling reads it.
  let currencyRefused = false;
  const refuseCurrency = (currency: string): void => {
    if (currencyRefused) return;
    currencyRefused = true;
    const code = displayableCode(currency);
    console.error("[dice] the session currency cannot be played by this game:", code ?? "(not printable)");
    if (driven) {
      if (G.S.auto) G.stopAuto("Currency not supported");
      G.setPaused(true);
    }
    const n = currencyNotice(code);
    blockApp(n.title, n.lines, "currency_unsupported", undefined, driven);
  };

  // Boot the balance (see the money-path ruling above): session.ready at boot, session.restored on a reload / 2nd
  // tab. the-handles.js animates each round on it; between rounds it is kept the server's by balance-sync.ts (below).
  // setMoney repaints the board, so the balance is set first: one paint, in the session's currency.
  const applySession = (s: SessionReadyPayload): void => {
    const playable = sessionMoney(s.currency);
    if (!playable) {
      refuseCurrency(s.currency);
      return;
    }
    money = playable;
    G.S.balance = playable.toGame(s.balanceMinor);
    G.setMoney(playable.format, playable.prefix);
  };
  sdk.on("session.ready", applySession);
  sdk.on("session.restored", applySession);
  // An unplayable currency (replayed on subscribe, just above): the boot stops here — no driver, no un-pause.
  if (currencyRefused) return;
  // error.fatal: at boot (replayed on subscribe, before any driver) or MID-PLAY (SC-62084, SDK >= 0.7.7: the platform
  // refused a call because the session is over; the SDK then denies every later play() without a request). Mid-play the
  // game is driven, so it is frozen first — the autobet run ends, the CTA goes to Paused (Space/clicks bail on S.paused)
  // and the driver refuses every round (fatal, below) — then covered by the relaunch notice.
  // The operator casino's signals (SC-61815; from the window that embeds the game, SC-62154 — bootstrap.ts): a pause or a block
  // ends the autobet run, pauses the game and holds every round; a resume releases it — and un-pauses only once the
  // game is driven and no fatal or reboot stands (a resume before the driver is installed must never un-freeze the
  // un-driven vendor). Subscribed now, before the reveal, so a hold that arrives while the loader covers the board is
  // kept by the un-pause at the end of the boot.
  const operatorHold = holdOnOperatorSignals(sdk, G, {
    mayResume: () => driven && !fatal && !halted && !currencyRefused && !early.rebooting(),
  });
  sdk.on("error.fatal", ({ code }) => {
    if (fatal) return; // one notice: the SDK raises it once per session, and a second panel would only stack
    fatal = true;
    console.error("[dice] SDK fatal:", code);
    if (currencyRefused) return; // the game is already stopped under its own notice
    if (driven) {
      if (G.S.auto) G.stopAuto("Session ended");
      G.setPaused(true);
    }
    const n = fatalNotice({ realLaunch: launch.realLaunch, resumable }, code);
    blockApp(n.title, n.lines, code, n.reload ? () => window.location.reload() : undefined, driven);
  });
  // A fatal raised inside createGameSDK (e.g. a REAL reload whose session is dead) replayed on subscribe, just above:
  // the boot stops here — no driver, no un-pause (with ?loader=none nothing else would guard the keys).
  if (fatal) return;
  // By the SDK's contract its boot has settled into a session snapshot or a fatal by now. With neither there is no
  // session to play and no currency to show the money in: the game never runs on a guess.
  if (money === null) {
    console.error("[dice] the SDK boot produced neither a session nor a fatal");
    showBootFailure(early);
    return;
  }
  // play.recovered: the SDK re-presents a round recovered on a reload with the balance it re-read — FUN only, for a boot
  // that declares FUN (SDK 0.7.4 / SC-61776; a REAL session never replays, and this game's FUN fake declares no mode, so
  // today it never fires). Apply that balance — boot time, no vendor round in flight, no double count — and ack it so
  // the SDK drops its pending record (client-local; the server is not told).
  sdk.on("play.recovered", ({ result }) => {
    if (result.status !== "settled") return;
    G.S.balance = sessionsMoney().toGame(result.balanceMinor);
    G.paint();
    sdk.reportResultsShown({ roundId: result.roundId });
  });

  // A SETTLED round whose roll cannot be proven (its reveal is missing, malformed or does not hash to its commitment): no
  // roll may be shown for it (SC-61813). The credited AMOUNT is never such a failure — the server may apply a limit, a
  // bonus or a denomination the game cannot see (pf/verify.ts). The money moved server-side, so the game stops — autobet
  // ended, paused, every later round refused — and the player reloads, which re-reads the balance. Reload works for
  // both modes: a REAL session's URL is #mode=real once it was ready.
  const halt = (autobetWhy: string, title: string, lines: readonly string[], code: string): void => {
    // Not while the page is leaving its launch (SC-61840): the game is already frozen, the SDK was stopped — which is
    // what cut the round — and the reload under way re-reads the balance. A notice would only flash before it.
    if (halted || fatal || early.rebooting()) return;
    halted = true;
    if (G.S.auto) G.stopAuto(autobetWhy);
    G.setPaused(true);
    blockApp(title, lines, code, () => window.location.reload(), true);
  };
  const haltUnverifiable = (): void =>
    halt("Round could not be verified", "We couldn't verify this round", ["Play is paused. Reload the game to check your balance."], "round_unverifiable");
  // A round whose OUTCOME IS UNKNOWN (SC-61817, round-outcome.ts): it neither settled nor was refused with a code that
  // proves no money moved, or nothing came back in time. The server may have debited, settled or refunded it, so the
  // game does not go on with a balance it cannot vouch for, and never says "try again" (a second wager). Same stop as
  // above; the reload re-reads the balance. The second line is for the refusals the SDK cannot tell from a failure —
  // the gateway's per-session wagering cap ends only with a new session (SC-62233).
  const haltUnconfirmed = (code: string): void =>
    halt(
      "Round not confirmed",
      "We couldn't confirm this round",
      ["Play is paused. Reload the game to check your balance.", "If this keeps happening, relaunch Dice from the casino."],
      code,
    );

  // Wire the operator into the vendor: the round driver, the verifier (CPDice.verify) and the provably-fair door's view
  // (the RGS's own per-round data instead of the prototype's local-seed panel).
  // The session's money as it stands (a later snapshot may renew it), for the round driver and the door. The boot
  // stopped above without one, so it is never missing here.
  const verify = makeVerifier(records);
  G.setVerifier(verify);
  // A ticket round is checked by the contract's verifier with the SDK's binding check of that same evidence: the one its
  // pf.proof carried, or the same check run here when no proof was heard (pf/ticket-verify.ts).
  const checkTicket = async (evidence: PfRoundEvidence, binding?: PfBindingResult) => verifyTicketRound(evidence, binding ?? (await checkPfBinding(evidence)));
  G.setFairView((body: HTMLElement) =>
    renderFairPanel(body, {
      mode: launch.realLaunch ? "real" : "demo",
      records,
      verify,
      formatMoney: (minor) => sessionsMoney().format(minor),
      formatWireMoney: (minor) => sessionsMoney().formatWire(minor), // a ticket's terms: the wire's units (SC-68493)
      tickets,
      checkTicket,
    }),
  );
  const toast = refusalToast(document);
  // SC-67337: on a short screen the product sheds its stake rail, and its own messages with it: the toast shows them.
  followStakeRail(document, toast);
  // The balance the board shows between rounds is the server's (SC-62290): a deposit, or a bet placed elsewhere, shows
  // without a reload. Installed with the driver — both need a session, and there is one here.
  const balance = syncBalance(document, window, G, sdk, { toGame: (wireMinor) => sessionsMoney().toGame(wireMinor) });
  const rollDriver = makeRollDriver({
    sdk,
    maxStakeForBet: async (winCount) => {
      const max = await maxStake.forBet(winCount); // SC-64981: read again where the roll is sent
      return typeof max === "number" ? sessionsMoney().toGame(max) : max;
    },
    getCurrency: () => sessionsMoney().currency,
    formatMinor: (minor) => sessionsMoney().format(minor),
    // SC-64981: the RGS stated the max stake of a selection — the game's ceiling takes it, so the refused roll's own repaint
    // clamps the stake to it; the words say the ceiling held after it
    holdMaxStake: (winCount, maxStakeMinor) => {
      maxStake.stated(winCount, maxStakeMinor); // the RGS's figure, in the wire's minor units
      const held = maxStake.rule(winCount);
      return held === null ? null : sessionsMoney().toGame(held);
    },
    toWire: (gameMinor) => sessionsMoney().toWire(gameMinor),
    payoutToGame: (wireMinor) => sessionsMoney().payoutToGame(wireMinor),
    records,
    refusal: () =>
      early.rebooting() || fatal || halted || currencyRefused || boardCovered(document)
        ? "BOARD_NOT_READY"
        : operatorHold.held()
          ? "OPERATOR_HOLD"
          : null,
    leaving: early.rebooting,
    onUnverifiable: haltUnverifiable,
    onUnconfirmed: ({ code }) => haltUnconfirmed(code),
    // In an autobet run the game's own run summary (its live region) announces the reason: the toast only shows it.
    onRefused: ({ code, message }) => {
      toast.show(message, { announce: !G.S.auto });
      // The wallet has less than the board shows (the player spent it elsewhere): show what it has.
      if (code === "insufficient_funds") balance.ask();
    },
    onSettled: (round) => balance.roundSettled(round),
  });
  // A new attempt takes the last refusal's reason off the screen (the game's own rail does the same when it repaints).
  G.setRollDriver((r: unknown) => {
    toast.clear();
    return rollDriver(r);
  });
  driven = true;
  // The board is driven and stays mounted from here on: its dialogs keep the keyboard (SC-60137) and it is laid out
  // again after a resize or a rotation (SC-60140).
  keepFocusInSheet(document);
  reflowOnResize(window, G);

  // The board is painted (the classic game script ran before this module) and the session balance is applied
  // (session.ready/restored replay on subscribe, above), so the game is ready to be shown. The SDK still gates the
  // reveal on ITS OWN latches (a fresh REAL launch waits for a valid session), so this can never reveal a REAL game
  // early. Called synchronously on purpose: a rAF would stall in a background tab past the SDK's 15s backstop and
  // strand a healthy game on the loader's terminal error surface.
  sdk.loader.progress(1);
  sdk.loader.ready();

  // UN-FREEZE the vendor (see the G.S.paused note at the top of main) now that the driver + verifier are installed,
  // through the game's own setPaused so the CTA repaints from the frozen "Paused" to a live "Roll" UNDER the loader —
  // the player never sees "Paused" during the reveal. A round the player cannot see still never reaches the RGS:
  //   - pointer input cannot reach the board: the SDK loader root covers the viewport on top until it is removed;
  //   - keyboard input (the game rolls on Space) is swallowed while the loader covers the board (blockKeysWhileCovered,
  //     which also keeps blocking behind the loader's terminal error surface);
  //   - the driver refuses any round attempted while the board is covered (refusal above, before play()).
  // Unless the operator holds the game (a pause/block that arrived during the boot): then only its resume un-pauses.
  blockKeysWhileCovered(document);
  if (!operatorHold.held()) G.setPaused(false);
}

